/**
 * FarmstandPage — public "auto-website" for a farm/farmstand.
 *
 * Route: /food-pulse/farm/:slug
 * Backend: GET /api/farms/profile/:slug
 *
 * Renders a warm, illustration-led public page for farm-type food
 * businesses. Uses the "Local Producer" illustration as hero backdrop
 * and lays out: story · seasonal calendar · badges · products (from
 * deals + inventory) · farm store · CSA · gallery · markets attended
 * · contact.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, MapPin, Phone, Mail, Globe, Calendar, Sprout, Award,
  Store, Truck, HeartHandshake, Instagram, Facebook, Youtube, ExternalLink,
  Sun, Loader2, Play, Image as ImageIcon,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import FarmerCropPoster from "@/components/FarmerCropPoster";

const API = process.env.REACT_APP_BACKEND_URL;
const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";

const MONTHS = [
  { key: "jan", label: "Jan" }, { key: "feb", label: "Feb" },
  { key: "mar", label: "Mar" }, { key: "apr", label: "Apr" },
  { key: "may", label: "May" }, { key: "jun", label: "Jun" },
  { key: "jul", label: "Jul" }, { key: "aug", label: "Aug" },
  { key: "sep", label: "Sep" }, { key: "oct", label: "Oct" },
  { key: "nov", label: "Nov" }, { key: "dec", label: "Dec" },
];

const BADGE_LABEL = {
  certified_organic:        "Certified Organic",
  family_owned:             "Family Owned",
  woman_owned:              "Woman Owned",
  small_farm_under_5_acres: "Small Farm (< 5 acres)",
  veteran_owned:            "Veteran Owned",
  regenerative:             "Regenerative",
  biodynamic:               "Biodynamic",
  pasture_raised:           "Pasture Raised",
  grass_fed:                "Grass Fed",
  heirloom_varieties:       "Heirloom Varieties",
  no_spray:                 "No Spray",
};

function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export default function FarmstandPage() {
  const { slug } = useParams();
  const [biz, setBiz] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([
      axios.get(`${API}/api/farms/profile/${slug}`),
      axios.get(`${API}/api/deals`, { params: { vendor_slug: slug, limit: 12 } })
        .catch(() => ({ data: { items: [] } })),
    ])
      .then(([b, d]) => {
        setBiz(b.data);
        setDeals(d.data.items || []);
      })
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}>
        <Loader2 className="w-10 h-10 animate-spin opacity-40" data-testid="farmstand-loading" />
      </div>
    );
  }
  if (notFound || !biz) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ background: CREAM }}>
        <div className="max-w-md text-center" data-testid="farmstand-not-found">
          <Sprout className="w-16 h-16 mx-auto opacity-30 mb-4" />
          <div className="font-display text-2xl mb-2">Farm not found</div>
          <Link to="/food-pulse/markets" className="underline" style={{ color: GREEN }}>
            ← Browse farmers markets
          </Link>
        </div>
      </div>
    );
  }

  const farm = biz.farm || {};
  const seasonal = farm.seasonal_calendar || {};
  const badges = farm.badges || [];
  const photos = [
    ...(biz.images || []),
    ...(farm.media_photos || []),
    ...((biz.photos_ai || []).map((p) => p.url).filter(Boolean)),
  ].filter(Boolean);
  const videos = farm.media_videos || [];
  const markets = biz.markets_attended_full || [];

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="farmstand-page">
      <SeoHead
        section="food-pulse"
        title={`${biz.name} — Farmstand · Food Pulse`}
        description={farm.tagline || biz.description || `Farm-fresh from ${biz.city || "our farm"}`}
        image={photos[0]}
      />

      {/* Hero — illustration backdrop with warm gradient overlay */}
      <section className="relative overflow-hidden" style={{ background: GREEN }}>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(115deg, rgba(15,61,46,0.92) 0%, rgba(15,61,46,0.72) 45%, rgba(15,61,46,0.35) 100%), url('/illustrations/food-categories/hero/local_producer.webp')`,
            backgroundSize: "cover",
            backgroundPosition: "center right",
          }}
          aria-hidden
        />
        <div className="relative max-w-5xl mx-auto px-4 py-14 sm:py-20 text-white">
          <Link
            to="/food-pulse/markets"
            className="inline-flex items-center gap-1 text-xs opacity-80 hover:opacity-100 mb-4"
            data-testid="farmstand-back"
          >
            <ArrowLeft className="w-3 h-3" /> Markets & farms
          </Link>
          <div className="inline-flex items-center gap-2 rounded-full border-2 px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.28em] mb-4"
               style={{ borderColor: AMBER, color: "#F6C87A" }}>
            <Sprout className="w-3.5 h-3.5" /> Farm · Farmstand
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="farmstand-name">
            {biz.name}
          </h1>
          {farm.tagline && (
            <p className="mt-4 text-lg opacity-90 max-w-2xl" data-testid="farmstand-tagline">{farm.tagline}</p>
          )}
          <div className="mt-4 flex flex-wrap gap-2 items-center opacity-90 text-sm">
            {(biz.city || biz.state) && (
              <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {[biz.city, biz.state_code || biz.state].filter(Boolean).join(", ")}</span>
            )}
            {farm.year_established && (
              <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" /> Since {farm.year_established}</span>
            )}
            {typeof farm.land_size_acres === "number" && (
              <span className="inline-flex items-center gap-1"><Sprout className="w-4 h-4" /> {farm.land_size_acres} acres</span>
            )}
          </div>
          {badges.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="farmstand-badges">
              {badges.map((b) => (
                <span key={b} className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold"
                      style={{ background: "#fff", color: GREEN }}>
                  <Award className="w-3.5 h-3.5" /> {BADGE_LABEL[b] || b.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          )}
          {farm.csa_available && (
            <div className="mt-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold"
                 style={{ background: AMBER, color: GREEN }}
                 data-testid="farmstand-csa-chip">
              <HeartHandshake className="w-4 h-4" /> CSA available
              {farm.csa_url && (
                <a href={farm.csa_url} target="_blank" rel="noreferrer" className="ml-2 underline inline-flex items-center gap-1">
                  Sign up <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-12 grid gap-8 lg:grid-cols-3">
        {/* Left column — story, seasonal, gallery, videos */}
        <div className="lg:col-span-2 space-y-8">
          {(farm.story || biz.long_description || biz.description) && (
            <section className="rounded-3xl bg-white border p-6 sm:p-8" style={{ borderColor: "#E9D8B4" }} data-testid="farmstand-story">
              <div className="text-[11px] font-mono uppercase tracking-widest mb-2" style={{ color: AMBER }}>— Our story</div>
              <p className="whitespace-pre-line text-base leading-relaxed opacity-90">
                {farm.story || biz.long_description || biz.description}
              </p>
              {(farm.growing_methods || []).length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {farm.growing_methods.map((g) => (
                    <span key={g} className="rounded-full px-3 py-1 text-[11px] font-mono uppercase tracking-widest"
                          style={{ background: "#F0F5F1", color: GREEN }}>
                      {g}
                    </span>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Seasonal calendar */}
          {Object.values(seasonal).some(Boolean) && (
            <section className="rounded-3xl bg-white border p-6 sm:p-8" style={{ borderColor: "#E9D8B4" }} data-testid="farmstand-calendar">
              <div className="text-[11px] font-mono uppercase tracking-widest mb-4" style={{ color: AMBER }}>— Seasonal calendar</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {MONTHS.map((m) => (
                  <div key={m.key}
                       className="rounded-2xl border p-3 text-sm"
                       style={{ borderColor: seasonal[m.key] ? GREEN : "#E9D8B4",
                                background: seasonal[m.key] ? "#F0F5F1" : "transparent" }}
                       data-testid={`calendar-${m.key}`}>
                    <div className="font-mono text-[10px] uppercase opacity-70 mb-1">{m.label}</div>
                    <div className={seasonal[m.key] ? "font-medium" : "opacity-40 italic"}>
                      {seasonal[m.key] || "—"}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Today's picks (deals) */}
          {deals.length > 0 && (
            <section className="rounded-3xl bg-white border p-6 sm:p-8" style={{ borderColor: "#E9D8B4" }} data-testid="farmstand-deals">
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color: AMBER }}>— Fresh today</div>
                  <h2 className="font-display text-2xl">What we're picking</h2>
                </div>
                <Link to="/food-pulse/deals" className="text-xs underline opacity-70 hover:opacity-100">See all deals</Link>
              </div>
              {/* One-tap "post today's harvest" form — pipes into
                  /api/farmers-pulse/crop-post which auto-drafts a matching
                  FoodPulse deal + fans out wishlist alerts. Rendered here
                  so farmers can post right where they see their deals. */}
              <div className="mb-5">
                <FarmerCropPoster
                  farmSlug={slug}
                  city={biz.city}
                  state={biz.state_code || biz.state}
                  country={biz.country}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {deals.map((d) => (
                  <Link key={d.slug} to={`/food-pulse/deals/${d.slug}`}
                        className="block rounded-2xl border overflow-hidden bg-white hover:-translate-y-0.5 hover:shadow-md transition-all"
                        style={{ borderColor: "#E9D8B4" }}
                        data-testid={`farmstand-deal-${d.slug}`}>
                    {d.food_item_image && (
                      <div className="aspect-[4/3] overflow-hidden"><img src={d.food_item_image} alt={d.title} className="w-full h-full object-cover" /></div>
                    )}
                    <div className="p-3">
                      <div className="font-semibold text-sm line-clamp-1">{d.title || d.food_item_name}</div>
                      {d.deal_price != null && (
                        <div className="mt-1 text-sm">
                          <span className="font-bold">${d.deal_price}</span>
                          {d.original_price && d.original_price > d.deal_price && (
                            <span className="ml-1 opacity-60 line-through text-xs">${d.original_price}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Video gallery */}
          {videos.length > 0 && (
            <section className="rounded-3xl bg-white border p-6 sm:p-8" style={{ borderColor: "#E9D8B4" }} data-testid="farmstand-videos">
              <div className="text-[11px] font-mono uppercase tracking-widest mb-4" style={{ color: AMBER }}>— Videos</div>
              <div className="grid gap-3 sm:grid-cols-2">
                {videos.slice(0, 4).map((v, i) => {
                  const yt = extractYouTubeId(v);
                  if (yt) {
                    return (
                      <div key={i} className="aspect-video rounded-xl overflow-hidden border" style={{ borderColor: "#E9D8B4" }}>
                        <iframe src={`https://www.youtube.com/embed/${yt}`} className="w-full h-full" title={`Video ${i + 1}`}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                      </div>
                    );
                  }
                  return (
                    <a key={i} href={v} target="_blank" rel="noreferrer"
                       className="aspect-video rounded-xl border flex items-center justify-center gap-2 text-sm hover:bg-black/5"
                       style={{ borderColor: "#E9D8B4" }}>
                      <Play className="w-5 h-5" /> Watch <ExternalLink className="w-3 h-3" />
                    </a>
                  );
                })}
              </div>
            </section>
          )}

          {/* Photo gallery */}
          {photos.length > 0 && (
            <section className="rounded-3xl bg-white border p-6 sm:p-8" style={{ borderColor: "#E9D8B4" }} data-testid="farmstand-gallery">
              <div className="text-[11px] font-mono uppercase tracking-widest mb-4" style={{ color: AMBER }}>— Gallery</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {photos.slice(0, 9).map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer"
                     className="aspect-square rounded-lg overflow-hidden border block" style={{ borderColor: "#E9D8B4" }}>
                    <img src={url} alt={`${biz.name} photo ${i + 1}`}
                         className="w-full h-full object-cover hover:scale-105 transition-transform" loading="lazy" />
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right column — farm store, markets, contact, socials */}
        <aside className="space-y-6">
          {farm.farm_store && farm.farm_store.is_open && (
            <div className="rounded-3xl border-2 p-6" style={{ borderColor: AMBER, background: "linear-gradient(180deg,#FFF7E4 0%,#fff 100%)" }} data-testid="farmstand-store">
              <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest mb-2" style={{ color: "#8B5E10" }}>
                <Store className="w-3.5 h-3.5" /> Farm store · open
              </div>
              <div className="font-display text-lg mb-2">Visit us on the farm</div>
              {farm.farm_store.pickup_hours && (
                <div className="text-sm mb-2"><span className="opacity-60">Hours:</span> {farm.farm_store.pickup_hours}</div>
              )}
              {farm.farm_store.address_note && (
                <div className="text-sm opacity-80 whitespace-pre-line">{farm.farm_store.address_note}</div>
              )}
              {farm.farm_store.external_shop_url && (
                <a href={farm.farm_store.external_shop_url} target="_blank" rel="noreferrer"
                   className="mt-3 inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold text-white"
                   style={{ background: GREEN }}
                   data-testid="farmstand-external-shop">
                  <Truck className="w-4 h-4" /> Shop online <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}

          {markets.length > 0 && (
            <div className="rounded-3xl bg-white border p-6" style={{ borderColor: "#E9D8B4" }} data-testid="farmstand-markets">
              <div className="text-[11px] font-mono uppercase tracking-widest mb-3" style={{ color: AMBER }}>— Find us at</div>
              <ul className="space-y-2 text-sm">
                {markets.map((m) => (
                  <li key={m.slug}>
                    <Link to={`/food-pulse/markets`} className="hover:underline flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 opacity-60 flex-none" />
                      <div>
                        <div className="font-medium">{m.name}</div>
                        <div className="text-xs opacity-70">{[m.city, m.state].filter(Boolean).join(", ")}</div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-3xl bg-white border p-6" style={{ borderColor: "#E9D8B4" }} data-testid="farmstand-contact">
            <div className="text-[11px] font-mono uppercase tracking-widest mb-3" style={{ color: AMBER }}>— Contact</div>
            <div className="space-y-2 text-sm">
              {biz.phone && <a href={`tel:${biz.phone}`} className="flex items-center gap-2 hover:underline"><Phone className="w-4 h-4 opacity-60" /> {biz.phone}</a>}
              {biz.email && <a href={`mailto:${biz.email}`} className="flex items-center gap-2 hover:underline"><Mail className="w-4 h-4 opacity-60" /> {biz.email}</a>}
              {biz.website && (
                <a href={biz.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline break-all">
                  <Globe className="w-4 h-4 opacity-60 flex-none" />
                  <span className="truncate">{biz.website.replace(/^https?:\/\//, "")}</span>
                  <ExternalLink className="w-3 h-3 opacity-60 flex-none" />
                </a>
              )}
              {!biz.phone && !biz.email && !biz.website && (
                <div className="text-xs opacity-60">Contact info not published yet.</div>
              )}
            </div>
          </div>

          {biz.social_media && Object.values(biz.social_media).some(Boolean) && (
            <div className="rounded-3xl bg-white border p-6" style={{ borderColor: "#E9D8B4" }} data-testid="farmstand-social">
              <div className="text-[11px] font-mono uppercase tracking-widest mb-3" style={{ color: AMBER }}>— Follow</div>
              <div className="flex flex-wrap gap-2">
                {biz.social_media.instagram && <a href={biz.social_media.instagram} target="_blank" rel="noreferrer" className="rounded-full px-3 py-1 border text-xs inline-flex items-center gap-1" style={{ borderColor: "#E9D8B4" }}><Instagram className="w-3.5 h-3.5" /> Instagram</a>}
                {biz.social_media.facebook && <a href={biz.social_media.facebook} target="_blank" rel="noreferrer" className="rounded-full px-3 py-1 border text-xs inline-flex items-center gap-1" style={{ borderColor: "#E9D8B4" }}><Facebook className="w-3.5 h-3.5" /> Facebook</a>}
                {biz.social_media.youtube && <a href={biz.social_media.youtube} target="_blank" rel="noreferrer" className="rounded-full px-3 py-1 border text-xs inline-flex items-center gap-1" style={{ borderColor: "#E9D8B4" }}><Youtube className="w-3.5 h-3.5" /> YouTube</a>}
              </div>
            </div>
          )}

          {farm.farm_visits_ok && (
            <div className="rounded-3xl border p-4 text-xs opacity-80" style={{ borderColor: "#E9D8B4", background: "#F0F5F1" }}>
              <Sun className="w-4 h-4 inline mr-1" style={{ color: GREEN }} />
              Farm visits welcome. Please call ahead to schedule.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
